import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription, map, take } from 'rxjs';

import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo, FileUploadError, FileUploadStatus } from '@vendasta/galaxy/uploader';

import { Validator } from '@vendasta/shared';
import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { jsonCopy } from '../../shared';

// Interface
export interface ImageAndTextBlock {
  layout?: 'largeHorizontal' | 'mediumHorizontal' | 'smallHorizontal' | 'LargeVertical';
  paddingBetween?: number;
  items?: ImageAndTextItem[];
}

export interface ImageAndTextItem {
  imageUrl?: string;
  heading?: string;
  subheading?: string;
  body?: string;
  linkUrl?: string;
  linkText?: string;
  collapsed?: boolean;
}

// Default blank value
export const blankImageAndTextBlock: ImageAndTextBlock = {
  layout: 'LargeVertical',
  paddingBetween: null,
  items: [
    {
      imageUrl: null,
      heading: null,
      subheading: null,
      body: null,
      linkUrl: null,
      linkText: null,
      collapsed: false,
    },
  ],
};

export const blankImageAndTextItem: ImageAndTextItem = {
  imageUrl: null,
  heading: null,
  subheading: null,
  body: null,
  linkUrl: null,
  linkText: null,
  collapsed: false,
};

@Component({
  selector: 'email-image-and-text-block',
  templateUrl: './image-and-text-block.component.html',
  styleUrls: ['./image-and-text-block.component.scss'],
  standalone: false,
})
export class ImageAndTextBlockComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'email-html-block';
  @Input() content: ImageAndTextBlock;
  @Output() isBlockValid = new EventEmitter<boolean>();
  @ViewChild('blockInputs') blockInputs: NgForm;

  public urlMailtoOrDynamicContentRegex = Validator.URL_MAILTO_OR_DYNAMIC_CONTENT_REGEX;

  FileUploadStatus = FileUploadStatus;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private emailPreview: EmailPreviewRefreshService,
    private readonly alerts: SnackbarService,
    private imageUploadService: ImageUploadService,
  ) {}

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.blockInputs.statusChanges.subscribe((status) => {
        this.isBlockValid.emit(status === 'VALID');
      }),
    );
    this.subscriptions.push(
      this.blockInputs.valueChanges.subscribe(() => {
        this.emailPreview.refresh();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fixFieldLosingFocusWhenTyping(index: any, item: any): any {
    // https://stackoverflow.com/a/60945501/7338673
    return index;
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.content.items, event.previousIndex, event.currentIndex);
    this.emailPreview.refresh();
  }

  addItem(): void {
    this.content.items.push(jsonCopy(blankImageAndTextItem));
    this.emailPreview.refresh();
  }

  removeItem(index: number): void {
    this.content.items.splice(index, 1);
    this.emailPreview.refresh();
  }

  addNewAbove(index: number): void {
    if (index > -1) {
      this.content.items.splice(index, 0, jsonCopy(blankImageAndTextItem));
    } else {
      this.content.items.push(jsonCopy(blankImageAndTextItem));
    }
    this.emailPreview.refresh();
  }

  duplicateItem(index: number): void {
    if (index > -1) {
      this.content.items.splice(index, 0, jsonCopy(this.content.items[index]));
    }
    this.emailPreview.refresh();
  }

  moveBlock(index, delta): void {
    const newIndex = index + delta;

    //Already at the top or bottom.
    if (newIndex < 0 || newIndex === this.content.items.length) return;

    moveItemInArray(this.content.items, index, newIndex);
    this.emailPreview.refresh();
  }

  moveBlockToTop(index): void {
    moveItemInArray(this.content.items, index, 0);
    this.emailPreview.refresh();
  }

  moveBlockToBottom(index): void {
    moveItemInArray(this.content.items, index, this.content.items.length);
    this.emailPreview.refresh();
  }

  setImage(file: FileInfo, arrayId: number): void {
    this.content.items[arrayId].imageUrl = file.url;
    this.emailPreview.refresh();
  }

  clearImage(arrayId: number): void {
    this.content.items[arrayId].imageUrl = null;
    this.emailPreview.refresh();
  }

  alertFileErrored($event: FileUploadError): void {
    console.error('Image upload failed', $event);
    this.alerts.openErrorSnack('IMAGE_UPLOAD_FAILURE');
  }

  async openSuggestImageDialog(arrayId: number): Promise<void> {
    await this.imageUploadService.generateAndUploadImages().then((selectedImage) => {
      if (selectedImage) {
        selectedImage
          .pipe(
            take(1),
            map((img) => {
              this.content.items[arrayId].imageUrl = img;
              this.emailPreview.refresh();
            }),
          )
          .subscribe();
      }
    });
  }
}
